import React, { useState } from 'react';
import { ClassMetrics } from '../types';
import { CLASS_BY_ID } from '../constants/landUseConfig';
import { fmtEur } from '../utils/formatters';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  classRows: ClassMetrics[];
}

export const ClassBreakdownTable: React.FC<Props> = ({ classRows }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex justify-between items-center hover:bg-gray-50 transition-colors"
      >
        <h2 className="text-sm font-semibold">Breakdown by Land Class</h2>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-600" />
        )}
      </button>
      
      {isExpanded && (
        <>
          <div className="px-3 pb-3 overflow-x-auto border-t">
            <table className="w-full text-xs font-mono mt-2">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-1.5 px-1.5">Class</th>
                  <th className="text-left py-1.5 px-1.5">ha</th>
                  <th className="text-left py-1.5 px-1.5">€/yr</th>
                  <th className="text-left py-1.5 px-1.5">tCO₂e</th>
                  <th className="text-left py-1.5 px-1.5">Bio%</th>
                  <th className="text-left py-1.5 px-1.5">Soil Δ%</th>
                </tr>
              </thead>
              <tbody>
                {classRows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="py-1.5 px-1.5">
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-3 h-3 border border-gray-900 flex-shrink-0"
                          style={{ backgroundColor: CLASS_BY_ID[r.id].fill }}
                        />
                        <span className="text-[10px]">{r.label.split(' ')[0]}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-1.5">{r.areaHa.toFixed(1)}</td>
                    <td className="py-1.5 px-1.5">{fmtEur(r.econ)}</td>
                    <td className="py-1.5 px-1.5">
                      {r.carbon >= 0 ? '+' : ''}
                      {r.carbon.toFixed(1)}
                    </td>
                    <td className="py-1.5 px-1.5">{r.avgBio.toFixed(1)}</td>
                    <td className="py-1.5 px-1.5">
                      {r.avgMod >= 0 ? '+' : ''}
                      {r.avgMod.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 pb-3 border-t bg-gray-50">
            <p className="text-[10px] text-gray-600 mt-2 leading-tight">
              "Soil Δ%" is the area-weighted productivity modifier currently applied to each class from
              the organic/mineral soil rules, which in turn scales that class's revenue, carbon and
              biodiversity figures.
            </p>
          </div>
        </>
      )}
    </div>
  );
};